<template>
  <div class="default-modal share-cart-modal">
    <div class="default-modal__header share-cart-modal__header">
      <h3 class="default-modal__title share-cart-modal__title">
        Это не ваша корзина — вы можете добавить её к себе
      </h3>
      <span class="share-cart-modal__count"
        >{{ localCartItems.length }} {{ getProductCountText(localCartItems.length) }}</span
      >
      <button type="button" class="default-modal__close share-cart-modal__close" @click="$emit('close')">
        <IconComponent name="close" class="default-modal__close share-cart-modal__close-icon" />
      </button>
    </div>
    <div class="default-modal__body share-cart-modal__body">
      <div class="share-cart-modal__content">
        <div class="share-cart-modal__products">
          <ProductsTableComponent
            :items="localCartItems"
            @remove="removeItem"
            @countChange="changeItemCount"
          />
        </div>

        <div class="share-cart-modal__total">
          <div class="share-cart-modal__total-row">
            <span class="share-cart-modal__total-label">Итого:</span>
            <span class="share-cart-modal__total-value">{{ totalPrice | formatPrice }}</span>
          </div>
        </div>
      </div>
    </div>
    <div class="default-modal__footer share-cart-modal__footer">
      <button
        v-if="isSharedCart"
        type="button"
        class="btn btn--md btn--main"
        :disabled="loading || localCartItems.length === 0"
        @click="addToCart"
      >
        <FileIconComponent v-if="loading" name="loading" />
        <template v-else>Добавить в свою корзину</template>
      </button>
    </div>
  </div>
</template>

<script>
import IconComponent from "components/IconComponent.vue";
import FileIconComponent from "components/FileIconComponent.vue";
import ProductsTableComponent from "components/ProductsTableComponent.vue";
import ImgComponent from "components/ImgComponent.vue";

export default {
  name: "ShareCartModal",
  components: {
    IconComponent,
    FileIconComponent,
    ProductsTableComponent,
    ImgComponent,
  },
  props: {
    cartItems: {
      type: Array,
      required: true,
    },
    isSharedCart: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      url: "",
      hash: "",
      copied: false,
      localCartItems: [],
    };
  },
  created() {
    this.localCartItems = [...this.cartItems];
  },
  computed: {
    totalPrice() {
      return this.localCartItems.reduce((total, item) => {
        if (!item || !item.item) return total;

        let price = item.item.price;
        if (item.item.join_share && item.item.join_share.share) {
          price = (price * (100 - parseFloat(item.item.join_share.share.percent))) / 100;
        }

        return total + price * (item.count || item.quantity || 1);
      }, 0);
    },
  },
  methods: {
    inCart(item) {
      return !!this.$store.state.auth.cart.find((c) => c.item?.id === item?.id);
    },

    removeItem(ids) {
      this.localCartItems = this.localCartItems.filter((item) => {
        if (!item) {
          return false;
        }

        const itemId = item.id;
        const shouldKeep = !ids.includes(itemId);
        return shouldKeep;
      });
    },

    changeItemCount(changes) {
      this.localCartItems = this.localCartItems
        .map((item) => {
          if (!item) return null;

          const itemId = item.item.id;

          if (itemId === changes[0].id) {
            return {
              ...item,
              count: changes[0].count,
            };
          }
          return item;
        })
        .filter((item) => item !== null && item !== undefined);
    },

    getProductCountText(count) {
      const lastDigit = count % 10;
      const lastTwoDigits = count % 100;

      if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
        return "товаров";
      }

      if (lastDigit === 1) {
        return "товар";
      }

      if (lastDigit >= 2 && lastDigit <= 4) {
        return "товара";
      }

      return "товаров";
    },

    async addToCart() {
      if (this.loading || this.localCartItems.length === 0) return;

      try {
        const items = this.localCartItems.map((item) => ({
          item: item.item,
          count: item.count,
        }));

        items.forEach((item) => {
          if (this.inCart(item.item)) {
            this.$store.state.auth.cart = this.$store.state.auth.cart.map((c) =>
              c.item?.id === item.item.id ? { ...c, count: c.count + item.count } : c
            );
          } else {
            this.$store.state.auth.cart.unshift({
              count: item.count,
              item: item.item,
            });
          }
        });

        const isAuthorized = !!this.$store.state.apollo_token;

        if (isAuthorized) {
          this.loading = true;

          this.$store
            .dispatch("CART_ADD_ITEMS", {
              apollo: this.$apollo,
              variables: {
                items: this.$store.state.auth.cart.map((item) => ({
                  id: item.item.id,
                  count: item.count,
                })),
              },
            })
            .then(() => {
              this.loading = false;
            })
            .catch((error) => {
              this.loading = false;
              console.error(error);
            });
        }

        this.$emit("close");

        this.$notify({
          type: "success",
          title: "Успешно",
          text: "Товары добавлены в вашу корзину",
        });

        this.$router.push({ name: "cart" });
      } catch (error) {
        console.error("Ошибка при добавлении товаров в корзину:", error);
        this.$notify({
          type: "error",
          title: "Ошибка",
          text: "Не удалось добавить товары в корзину",
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="stylus">
.share-cart-modal {
    padding: 50px;
		width: 100vw;
		max-width: 1010px;

    &__header {
        border-bottom: none;
        padding: 0;
        justify-content: flex-start;

        & span {
            color: var(--main-color);
        }
    }

    &__title {
        font-size: 22px;
        font-weight: 600;
        text-transform: none;
    }

    &__body {
        padding: 0;
        margin-top 40px
    }

    &__close-icon {
        transform: translate(-10%, -10%) rotate(45deg);
    }

    &__footer {
        padding: 0;
        margin-top 40px
    }

    &__content {
        display: grid;
        grid-gap: 20px;
				width: 100%;
    }

    &__url {
        margin-bottom: 10px;

        p {
            margin-bottom: 10px;
            font-weight: 500;
        }
    }

    &__url-container {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    &__url-input {
        flex: 1;
        padding: 10px;
        border: 1px solid var(--gray);
        border-radius: var(--main_radius);
        font-size: 14px;
        background: var(--gray_o5);
        color: var(--body-color);
    }

    &__products {
        h4 {
            margin-bottom: 15px;
            font-weight: 500;
        }
    }

    &__total {
        margin-top: 30px;
        padding-top: 20px;
    }

    &__total-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 10px;
    }

    &__total-label {
        font-size: 22px;
        font-weight: 600;
    }

    &__total-value {
        font-size: 22px;
        font-weight: 600;
        color: #000;
    }
}
</style>
